import React from "react";

import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  Skeleton,
  Button,
  TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { getCurrency } from "../../Utils/currency";
import { useGetAllCartItemsQuery } from "../../Store/Services/CartService";
import { useUpdateRefundStatusMutation } from "../../Store/Services/orderService";
import { toast } from "react-toastify";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE_URL;

const InfoCard = ({ icon, title, children }) => {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        background: "#fff",
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          {icon}

          <Typography fontWeight={700} fontSize={15}>
            {title}
          </Typography>
        </Stack>

        {children}
      </CardContent>
    </Card>
  );
};

const LabelValue = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography color="#64748b" fontSize={14}>
      {label}
    </Typography>

    <Typography fontWeight={600} fontSize={14} textAlign="right">
      {value || "-"}
    </Typography>
  </Box>
);

const OrderDetailsDialog = ({ open, onClose, cartId, onReprint }) => {
  const { data, isLoading, isError } = useGetAllCartItemsQuery(cartId, {
    skip: !cartId || !open,
  });
  const [updateRefundStatus] = useUpdateRefundStatusMutation();

  const [requestType, setRequestType] = React.useState("");
  const [requestRemarks, setRequestRemarks] = React.useState("");
  const [requestBusy, setRequestBusy] = React.useState(false);

  const order = data?.orderDetails;
const { symbol: currencySymbol = "" } = getCurrency();

  const openRequestDialog = (type) => {
    setRequestType(type);
    setRequestRemarks("");
  };

  const closeRequestDialog = () => {
    if (requestBusy) return;
    setRequestType("");
    setRequestRemarks("");
  };

  const submitRequest = async () => {
    if (!order?.items?.length) {
      toast.error("No items found in this order.", { position: "bottom-right" });
      return;
    }

    setRequestBusy(true);
    const statusText = requestType === "refund" ? "refund_requested" : "return_requested";
    const remarks = requestRemarks.trim();

    try {
      await Promise.all(
        order.items
          .filter((item) => item?.orderDetId)
          .map((item) =>
            updateRefundStatus({
              orderDetId: item.orderDetId,
              refundStatus: statusText,
              remarks,
            }).unwrap(),
          ),
      );

      toast.success(
        requestType === "refund"
          ? "Refund request submitted."
          : "Return request submitted.",
        { position: "bottom-right" },
      );
      closeRequestDialog();
    } catch {
      toast.info(
        requestType === "refund"
          ? "Refund request noted. Verify backend response."
          : "Return request noted. Verify backend response.",
        { position: "bottom-right" },
      );
      closeRequestDialog();
    } finally {
      setRequestBusy(false);
    }
  };

  const handleCopySummary = async () => {
    if (!order) return;
    const summary = [
      `Order #${order.orderHeadId}`,
      `Customer: ${order.custName || "-"}`,
      `Mobile: ${order.mobileDel || order.mobile || "-"}`,
      `Items: ${order.totalQty || 0}`,
      `Total Paid: ${currencySymbol} ${Number(order.totPay || 0).toFixed(2)}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(summary);
      toast.success("Order summary copied.", { position: "bottom-right" });
    } catch {
      toast.error("Unable to copy summary.", { position: "bottom-right" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      {isLoading && (
        <DialogContent
          sx={{
            p: 4,
          }}
        >
          <Skeleton height={80} />

          <Skeleton height={300} />
        </DialogContent>
      )}

      {isError || !order ? (
        !isLoading && (
          <DialogContent
            sx={{
              p: 5,
              textAlign: "center",
            }}
          >
            <Typography color="error" fontWeight={600}>
              Unable to load order details.
            </Typography>
          </DialogContent>
        )
      ) : (
        <>
          {/* HEADER */}

          <Box
            sx={{
              p: 3,

              color: "#fff",

              background: "linear-gradient(135deg,#2563eb,#4f46e5)",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",

                right: 15,

                top: 15,

                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography fontSize={25} fontWeight={800}>
              Order #{order.orderHeadId}
            </Typography>

            <Typography
              sx={{
                opacity: 0.85,
                mt: 0.5,
              }}
            >
              {order.custName}
            </Typography>

            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              <Chip
                label={`Payment: ${order.paymentMethod}`}
                sx={{
                  background: "#ffffff22",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />

              <Chip
                label={order.deliveryTyp}
                sx={{
                  background: "#ffffff22",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />

              <Chip
                label={`${order.totalQty} Item`}
                sx={{
                  background: "#ffffff22",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                onClick={() => onReprint && onReprint()}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
              >
                Reprint Bill
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => openRequestDialog("refund")}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#fff", borderColor: "#ffffff66" }}
              >
                Refund
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => openRequestDialog("return")}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#fff", borderColor: "#ffffff66" }}
              >
                Return
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleCopySummary}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, color: "#fff" }}
              >
                Copy Summary
              </Button>
            </Stack>
          </Box>

          <DialogContent
            sx={{
              p: 3,
              background: "#f8fafc",
            }}
          >
            {/* CUSTOMER / DELIVERY / BILLING */}

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={<PersonOutlineIcon color="primary" />}
                  title="Customer"
                >
                  <LabelValue label="Name" value={order.custName} />

                  <LabelValue label="Mobile" value={order.mobileDel} />

                  <LabelValue label="Email" value={order.delEmail} />
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={<LocalShippingOutlinedIcon color="primary" />}
                  title="Delivery"
                >
                  <LabelValue label="Address" value={order.deliveryAddress} />

                  <LabelValue label="Area" value={order.delAreaName} />

                  <LabelValue label="State" value={order.delStateName} />

                  <LabelValue label="Country" value={order.delCountryName} />
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <InfoCard
                  icon={<ReceiptLongOutlinedIcon color="primary" />}
                  title="Invoice"
                >
                  <LabelValue label="Address" value={order.invoicingAddress} />

                  <LabelValue label="Area" value={order.invoAreaName} />

                  <LabelValue label="State" value={order.invoStateName} />

                  <LabelValue label="Country" value={order.invoCountryName} />
                </InfoCard>
              </Grid>
            </Grid>

            {/* PRODUCTS */}

            <Card
              elevation={0}
              sx={{
                borderRadius: 3,

                border: "1px solid #e5e7eb",

                mb: 3,
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <ShoppingBagOutlinedIcon color="primary" />

                  <Typography fontWeight={800}>Products</Typography>
                </Stack>

                {order.items?.map((item) => (
                  <Box
                    key={item.orderDetId}
                    sx={{
                      display: "flex",

                      gap: 2,

                      p: 2,

                      mb: 2,

                      borderRadius: 3,

                      background: "#f8fafc",
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={
                        item.imageUrl
                          ? `${IMAGE_BASE}/${item.imageUrl.split(",")[0]}`
                          : ""
                      }
                      sx={{
                        width: 90,

                        height: 90,
                      }}
                    />

                    <Box flex={1}>
                      <Typography fontWeight={800}>{item.title}</Typography>

                      <Typography color="text.secondary" fontSize={14} mt={0.5}>
                        Quantity : {item.qty}
                      </Typography>

                      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                        {item.variants?.map((variant, index) => (
                          <Chip
                            key={index}
                            size="small"
                            label={`${variant.variant}: ${variant.variantValue}`}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>

<Typography fontWeight={800} fontSize={18}>
  {currencySymbol} {Number(item.amt).toFixed(2)}
</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              {/* TAX */}

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,

                    border: "1px solid #e5e7eb",
                  }}
                >
                  <CardContent>
                    <Typography fontWeight={800} mb={2}>
                      Taxes
                    </Typography>

                    {order.taxDetails?.map((tax) => (
                      <LabelValue
                        key={tax.taxId}
                        label={tax.tax}
                        value={`${currencySymbol} ${Number(tax.taxAmt).toFixed(2)}`}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>

              {/* CHARGES */}

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,

                    border: "1px solid #e5e7eb",
                  }}
                >
                  <CardContent>
                    <Typography fontWeight={800} mb={2}>
                      Charges
                    </Typography>

                    {order.chargeDetails?.map((charge) => (
                      <LabelValue
                        key={charge.chargeId}
                        label={charge.charge}
                        value={`${currencySymbol} ${Number(charge.chargeAmt).toFixed(2)}`}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* PAYMENT SUMMARY */}

            <Card
              elevation={0}
              sx={{
                mt: 3,

                borderRadius: 3,

                border: "1px solid #dbeafe",

                background: "#eff6ff",
              }}
            >
              <CardContent>
                <Typography fontWeight={900} fontSize={18} mb={2}>
                  Payment Summary
                </Typography>

                <LabelValue
                  label="Subtotal"
                  value={`${currencySymbol} ${Number(order.totAmt).toFixed(2)}`}
                />

                <LabelValue
                  label="Tax"
                 value={`${currencySymbol} ${Number(order.totTax).toFixed(2)}`}
                />

                <LabelValue
                  label="Charges"
                  value={`${currencySymbol} ${Number(order.totChg).toFixed(2)}`}
                />

                <LabelValue
                  label="Delivery"
                  value={`${currencySymbol} ${Number(order.delCost).toFixed(2)}`}
                />

                <Divider
                  sx={{
                    my: 2,
                  }}
                />

                <Box display="flex" justifyContent="space-between">
                  <Typography fontWeight={900} fontSize={20}>
                    Total Paid
                  </Typography>

<Typography fontWeight={900} fontSize={24} color="primary">
  {currencySymbol} {Number(order.totPay).toFixed(2)}
</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* NOTES */}

            {(order.customerRemarks ||
              order.adminRemarks ||
              order.deliveryNotes) && (
              <Card
                elevation={0}
                sx={{
                  mt: 3,

                  borderRadius: 3,

                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent>
                  <Typography fontWeight={800} mb={2}>
                    Notes
                  </Typography>

                  {order.customerRemarks && (
                    <LabelValue
                      label="Customer"
                      value={order.customerRemarks}
                    />
                  )}

                  {order.adminRemarks && (
                    <LabelValue label="Admin" value={order.adminRemarks} />
                  )}

                  {order.deliveryNotes && (
                    <LabelValue label="Delivery" value={order.deliveryNotes} />
                  )}
                </CardContent>
              </Card>
            )}
          </DialogContent>
        </>
      )}

      <Dialog open={Boolean(requestType)} onClose={closeRequestDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {requestType === "refund" ? "Request Refund" : "Request Return"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#5f7597", fontSize: "0.9rem", mb: 1 }}>
            Add optional remarks for this request.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={requestRemarks}
            onChange={(event) => setRequestRemarks(event.target.value)}
            placeholder="Type reason or additional notes"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRequestDialog} disabled={requestBusy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitRequest}
            disabled={requestBusy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {requestBusy ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OrderDetailsDialog;
